import { useState, useCallback, useRef, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';

interface PollingConfig {
  projectId: string;
  interval?: number; // Poll every X ms
  maxAttempts?: number; // Max poll attempts
  duration?: number; // Or poll for X ms total (alternative to maxAttempts)
}

export const useShipmentsPolling = () => {
  const queryClient = useQueryClient();
  const [isPolling, setIsPolling] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const attemptCountRef = useRef(0);

  /**
   * Start polling after a mutation
   */

  const startPolling = useCallback(
    (config: PollingConfig) => {
      const { interval = 2000, maxAttempts = 3, duration, projectId } = config;

      // Clear existing timers
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);

      setIsPolling(true);
      attemptCountRef.current = 0;

      // Immediate first refetch
      queryClient.invalidateQueries({
        queryKey: ['shipments', 'project', projectId, ''],
        exact: false,
      });

      // Setup interval polling
      intervalRef.current = setInterval(() => {
        attemptCountRef.current += 1;
        queryClient.invalidateQueries({
          queryKey: ['shipments', 'project', projectId, ''],
          exact: false,
        });

        // Stop after max attempts
        if (maxAttempts && attemptCountRef.current >= maxAttempts) {
          stopPolling();
        }
      }, interval);

      // Or stop after duration
      if (duration) {
        timeoutRef.current = setTimeout(() => {
          stopPolling();
        }, duration);
      }
    },
    [queryClient],
  );

  /**
   * Stop polling
   */
  const stopPolling = useCallback(() => {
    setIsPolling(false);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    attemptCountRef.current = 0;
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  return {
    isPolling,
    startPolling,
    stopPolling,
    attemptCount: attemptCountRef.current,
  };
};
